<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import ReportMetricCard from 'dashboard/routes/dashboard/settings/reports/components/ReportMetricCard.vue';
import {
  formatNumber,
  formatPercent,
  formatCurrencyValue,
  formatDuration,
  computeTrend,
} from 'kanban/helper/reportFormatters';

const props = defineProps({
  data: {
    type: Object,
    default: null,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  locale: {
    type: String,
    default: 'en',
  },
});

const emit = defineEmits(['retry']);

const { t } = useI18n();

const current = computed(() => props.data?.current || {});
const previous = computed(() => props.data?.previous || {});

// Top open pipeline value across currencies (never summed across currencies); when more
// than one currency is present we surface the dominant one and a "+N" marker.
const dominantValue = key => {
  const list = props.data?.value_by_currency || [];
  if (!list.length) {
    return {
      value: formatCurrencyValue(0, props.data?.currency, props.locale),
      extra: 0,
    };
  }
  const sorted = [...list].sort((a, b) => b[key] - a[key]);
  const top = sorted[0];
  const previousTop = (props.data?.previous_value_by_currency || []).find(
    entry => entry.currency === top.currency
  );
  return {
    value: formatCurrencyValue(top[key], top.currency, props.locale, {
      compact: true,
    }),
    extra: sorted.length - 1,
    trend: computeTrend(top[key], previousTop?.[key]),
  };
};

const winRateInfo = computed(() =>
  props.data && props.data.has_loss_stage === false
    ? t('KANBAN.REPORTS.SUMMARY.WIN_RATE_NO_LOSS_INFO')
    : t('KANBAN.REPORTS.SUMMARY.WIN_RATE_INFO')
);

const cards = computed(() => {
  const cur = current.value;
  const prev = previous.value;
  const pipeline = dominantValue('pipeline_value');
  const weighted = dominantValue('weighted_pipeline_value');

  return [
    {
      key: 'deals_won',
      label: t('KANBAN.REPORTS.SUMMARY.DEALS_WON'),
      info: t('KANBAN.REPORTS.SUMMARY.DEALS_WON_INFO'),
      value: formatNumber(cur.deals_won, props.locale),
      trend: computeTrend(cur.deals_won, prev.deals_won),
    },
    {
      key: 'deals_lost',
      label: t('KANBAN.REPORTS.SUMMARY.DEALS_LOST'),
      info: t('KANBAN.REPORTS.SUMMARY.DEALS_LOST_INFO'),
      value: formatNumber(cur.deals_lost, props.locale),
      trend: computeTrend(cur.deals_lost, prev.deals_lost),
      invert: true,
    },
    {
      key: 'win_rate',
      label: t('KANBAN.REPORTS.SUMMARY.WIN_RATE'),
      info: winRateInfo.value,
      value:
        cur.win_rate === null || cur.win_rate === undefined
          ? t('KANBAN.REPORTS.SUMMARY.WIN_RATE_NA')
          : formatPercent(cur.win_rate, props.locale),
    },
    {
      key: 'deals_created',
      label: t('KANBAN.REPORTS.SUMMARY.DEALS_CREATED'),
      info: t('KANBAN.REPORTS.SUMMARY.DEALS_CREATED_INFO'),
      value: formatNumber(cur.deals_created, props.locale),
      trend: computeTrend(cur.deals_created, prev.deals_created),
    },
    {
      key: 'pipeline_value',
      label: t('KANBAN.REPORTS.SUMMARY.PIPELINE_VALUE'),
      info: t('KANBAN.REPORTS.SUMMARY.PIPELINE_VALUE_INFO'),
      value: pipeline.value,
      extra: pipeline.extra,
      trend: pipeline.trend,
    },
    {
      key: 'weighted_pipeline_value',
      label: t('KANBAN.REPORTS.SUMMARY.WEIGHTED_PIPELINE_VALUE'),
      info: t('KANBAN.REPORTS.SUMMARY.WEIGHTED_PIPELINE_VALUE_INFO'),
      value: weighted.value,
      extra: weighted.extra,
      trend: weighted.trend,
    },
    {
      key: 'avg_lead_time',
      label: t('KANBAN.REPORTS.SUMMARY.AVG_LEAD_TIME'),
      info: t('KANBAN.REPORTS.SUMMARY.AVG_LEAD_TIME_INFO'),
      value: formatDuration(cur.avg_lead_time, t),
    },
    {
      key: 'avg_cycle_time',
      label: t('KANBAN.REPORTS.SUMMARY.AVG_CYCLE_TIME'),
      info: t('KANBAN.REPORTS.SUMMARY.AVG_CYCLE_TIME_INFO'),
      value: formatDuration(cur.avg_cycle_time, t),
    },
  ];
});

const trendClass = card => {
  if (!card.trend || card.trend.direction === 'flat') return 'text-n-slate-10';
  const positive = card.trend.direction === 'up' ? !card.invert : card.invert;
  return positive ? 'text-n-teal-11' : 'text-n-ruby-11';
};

const trendIcon = card => {
  if (!card.trend || card.trend.direction === 'flat') return 'i-lucide-minus';
  return card.trend.direction === 'up'
    ? 'i-lucide-trending-up'
    : 'i-lucide-trending-down';
};
</script>

<template>
  <div
    class="flex flex-col gap-4 p-5 border rounded-xl border-n-slate-3 bg-n-alpha-1"
  >
    <div
      v-if="isLoading"
      class="grid grid-cols-2 gap-6 md:grid-cols-4 animate-pulse"
    >
      <div v-for="i in 8" :key="i" class="flex flex-col gap-2">
        <div class="h-3 w-20 rounded bg-n-slate-4" />
        <div class="h-7 w-16 rounded bg-n-slate-4" />
      </div>
    </div>

    <div
      v-else-if="error"
      class="flex flex-col items-center justify-center gap-3 py-8 text-center"
    >
      <i class="i-lucide-alert-triangle w-6 h-6 text-n-amber-11" />
      <p class="text-sm text-n-slate-11">
        {{ t('KANBAN.REPORTS.WIDGET.ERROR_TITLE') }}
      </p>
      <Button
        size="xs"
        variant="faded"
        color="slate"
        icon="i-lucide-rotate-cw"
        :label="t('KANBAN.REPORTS.WIDGET.RETRY')"
        @click="emit('retry')"
      />
    </div>

    <div v-else class="grid grid-cols-2 gap-6 md:grid-cols-4">
      <div v-for="card in cards" :key="card.key" class="flex flex-col gap-1">
        <ReportMetricCard
          :label="card.label"
          :value="card.value"
          :info-text="card.info"
        />
        <div class="flex items-center gap-2 min-h-[1.25rem]">
          <span
            v-if="card.trend"
            class="flex items-center gap-1 text-xs font-medium"
            :class="trendClass(card)"
          >
            <i :class="trendIcon(card)" class="w-3.5 h-3.5" />
            {{ formatPercent(card.trend.ratio, locale) }}
            <span class="text-n-slate-10 font-normal">
              {{ t('KANBAN.REPORTS.SUMMARY.TREND_VS_PREVIOUS') }}
            </span>
          </span>
          <span v-else-if="card.extra" class="text-xs text-n-slate-10">
            {{
              t('KANBAN.REPORTS.SUMMARY.MULTI_CURRENCY', { count: card.extra })
            }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
